import { formatCubeValue, isNumericCubeFormat } from './cube_format.utils';
import { tablePivotForPaginatedData, shouldUsePivot } from './pagination_pivot.utils';
import { getColumnStyle } from './style_helpers.utils';
import { normalizeCubeFilterParam } from '../../dashboards/utils/dashboard_filters.utils';
export const prettyJson = (input) => JSON.stringify(input, null, 1) // Generates JSON with slight indentation
    .replace(/\[\s+/g, '[ ') // Remove space before opening array bracket
    .replace(/\s+\]/g, ' ]'); // Remove space after closing array bracket
export const parseJsonFromUrlParams = (param, defaultValue) => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryParam = urlParams.get(param);
    if (!queryParam) {
        return defaultValue;
    }
    try {
        return JSON.parse(queryParam);
    }
    catch (e) {
        console.error('Failed to parse query parameter:', e);
        return defaultValue;
    }
};
export const getInitialChartType = (defaultType = 'bar') => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('chartType') || defaultType;
};
export const processResultSet = (resultSet, key) => {
    return resultSet.tablePivot().map((row) => {
        const value = row[key];
        if (value === null || value === undefined) {
            return null;
        }
        return {
            label: row[key],
            value: row[key],
        };
    });
};
const formatValueWithUnit = (value, suffix, locale, columnType, columnStyle) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const trimmedSuffix = suffix?.trim() || '';
    const hasPercentageSuffix = trimmedSuffix.includes('%');
    const hasPercentageUnit = columnStyle?.unit?.value?.includes('%');
    const isPercentage = hasPercentageSuffix || hasPercentageUnit;
    if (columnType === 'string' && !isPercentage) {
        return suffix ? `${value}${suffix.trim()}` : value;
    }
    let formattedValue = value;
    if (hasPercentageSuffix || hasPercentageUnit) {
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;
        if (typeof numericValue === 'number' && !isNaN(numericValue)) {
            const rounded = Math.round(numericValue * 100) / 100;
            let roundedString = rounded.toFixed(2);
            roundedString = roundedString.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
            const result = suffix ? `${roundedString}${suffix.trim()}` : roundedString;
            return result;
        }
    }
    if (typeof value === 'number') {
        formattedValue = new Intl.NumberFormat(locale, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
        }).format(value);
    }
    else if (typeof value === 'string' && !isNaN(Number(value))) {
        const numValue = parseFloat(value);
        formattedValue = new Intl.NumberFormat(locale, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
        }).format(numValue);
    }
    return suffix ? `${formattedValue}${suffix.trim()}` : formattedValue;
};
/**
 * Resolves the cell value written to the export for a single column.
 * When `keepFormat` is false (raw export), the underlying Cube value is returned untouched so the
 * CSV carries raw numbers instead of display formatting (percentages, K/M abbreviations, units).
 */
const resolveExportCellValue = ({ value, format, suffix, columnType, columnStyle, locale, keepFormat, }) => {
    if (!keepFormat) {
        return value;
    }
    return isNumericCubeFormat(format)
        ? formatCubeValue(value, format, locale)
        : formatValueWithUnit(value, suffix, locale, columnType, columnStyle);
};
export const getTransformedExportData = ({ exportResultSets, chartData, isTableChart, locale, keepFormat = true, showFullTimestamp = true, }) => {
    const formatTimeValue = (value) => (showFullTimestamp ? parseISODateTime(value) : parseISODate(value));
    const combinedData = isTableChart
        ? exportResultSets.flatMap((rs) => rs?.tablePivot() || [])
        : chartData?.pivotConfig && shouldUsePivot(chartData.pivotConfig)
            ? tablePivotForPaginatedData(exportResultSets, chartData.pivotConfig)
            : exportResultSets.flatMap((rs) => rs?.rawData() || []);
    if (!combinedData || combinedData.length === 0) {
        throw new Error('No data found');
    }
    const tableColumns = isTableChart
        ? exportResultSets[0]?.tableColumns(undefined)
        : exportResultSets[0]?.tableColumns(chartData?.pivotConfig);
    if (!tableColumns) {
        throw new Error('No columns found');
    }
    const allAnnotations = exportResultSets[0]?.annotation() || {};
    const annotations = allAnnotations.measures || {};
    const dimensionAnnotations = allAnnotations.dimensions || {};
    const chartStyle = chartData?.style;
    const jsonUnit = chartStyle?.unit?.value ?? undefined;
    const columnsStyles = Array.isArray(chartData?.style) ? chartData.style : undefined;
    const keyToTitleMap = tableColumns.reduce((acc, column) => {
        const annotation = annotations[column.key] || {};
        const annotationMeta = annotation?.meta;
        const suffixFromAnnotation = annotationMeta?.suffix;
        const suffixFromJson = jsonUnit || '';
        const annotationFormat = annotation?.format;
        const suffixFromFormat = annotationFormat === 'percent' && !suffixFromAnnotation ? '%' : '';
        acc[column.key] = {
            title: column.shortTitle || column.key,
            suffix: suffixFromAnnotation || suffixFromJson || suffixFromFormat,
            format: annotationFormat,
        };
        if (column.children && column.children.length > 0) {
            column.children.forEach((child) => {
                const childAnnotation = annotations[child.key] || {};
                const childAnnotationMeta = childAnnotation?.meta;
                const childSuffixFromAnnotation = childAnnotationMeta?.suffix;
                const childAnnotationFormat = childAnnotation?.format;
                const childSuffixFromFormat = childAnnotationFormat === 'percent' && !childSuffixFromAnnotation ? '%' : '';
                let cleanTitle = child.shortTitle || child.title;
                if (!cleanTitle && child.key.includes('.')) {
                    cleanTitle = child.key.split('.').pop() || '';
                    cleanTitle = cleanTitle
                        .split('_')
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                }
                acc[child.key] = {
                    title: cleanTitle || child.key,
                    suffix: childSuffixFromAnnotation || suffixFromJson || childSuffixFromFormat,
                    format: childAnnotationFormat,
                };
            });
        }
        return acc;
    }, {});
    return combinedData.map((row) => {
        const newRow = {};
        const xColumn = tableColumns.find((col) => dimensionAnnotations[col.key] && !annotations[col.key]) ||
            tableColumns.find((col) => col.type === 'string') ||
            tableColumns[0];
        const isXAxisDimension = xColumn && dimensionAnnotations[xColumn.key] && !annotations[xColumn.key];
        const isStackedChart = tableColumns.some((col) => col.children && col.children.length > 0);
        if (isStackedChart) {
            tableColumns.forEach((column) => {
                if (column.children && column.children.length > 0) {
                    column.children.forEach((child) => {
                        const { dataIndex } = child;
                        if (dataIndex && dataIndex in row) {
                            const parentTitle = keyToTitleMap[column.key]?.title || column.key;
                            const childTitle = keyToTitleMap[child.key]?.title || child.key;
                            let suffix = keyToTitleMap[child.key]?.suffix || '';
                            suffix = suffix ? ` ${suffix}` : '';
                            let value = row[dataIndex];
                            if (child.type === 'time') {
                                value = formatTimeValue(value);
                            }
                            if (!value) {
                                newRow[`${parentTitle} - ${childTitle}`] = '';
                            }
                            else {
                                const childFormat = keyToTitleMap[child.key]?.format;
                                const childColumnStyle = getColumnStyle(child.key, columnsStyles);
                                newRow[`${parentTitle} - ${childTitle}`] = resolveExportCellValue({
                                    value,
                                    format: childFormat,
                                    suffix,
                                    columnType: child.type,
                                    columnStyle: childColumnStyle,
                                    locale,
                                    keepFormat,
                                });
                            }
                        }
                    });
                }
                else if (column.key in row) {
                    const colTitle = keyToTitleMap[column.key]?.title || column.key;
                    let suffix = keyToTitleMap[column.key]?.suffix || '';
                    suffix = suffix ? ` ${suffix}` : '';
                    let value = row[column.key];
                    if (column.type === 'time') {
                        value = formatTimeValue(value);
                    }
                    if (!value) {
                        newRow[colTitle] = '';
                    }
                    else {
                        const colFormat = keyToTitleMap[column.key]?.format;
                        const columnStyle = getColumnStyle(column.key, columnsStyles);
                        newRow[colTitle] = resolveExportCellValue({
                            value,
                            format: colFormat,
                            suffix,
                            columnType: column.type,
                            columnStyle,
                            locale,
                            keepFormat,
                        });
                    }
                }
            });
        }
        else {
            const dimensions = tableColumns.filter((col) => dimensionAnnotations[col.key] && !annotations[col.key]);
            const measures = tableColumns.filter((col) => (col.key !== xColumn?.key || !isXAxisDimension) &&
                (annotations[col.key] || (!dimensionAnnotations[col.key] && col.type !== 'string')));
            const sortedColumns = [...dimensions, ...measures];
            sortedColumns.forEach((column) => {
                const colKey = column.key;
                if (column.key === xColumn?.key && isXAxisDimension) {
                    const xHeader = keyToTitleMap[xColumn.key]?.title || xColumn.key;
                    const xValue = row.x ?? row.xValues?.[0] ?? row[xColumn.key] ?? '';
                    if (xValue !== '') {
                        newRow[xHeader] = xValue;
                    }
                }
                else if (colKey in row) {
                    const colTitle = keyToTitleMap[colKey]?.title || colKey;
                    let suffix = keyToTitleMap[colKey]?.suffix || '';
                    suffix = suffix ? ` ${suffix}` : '';
                    let value = row[colKey];
                    if (column.type === 'time') {
                        value = formatTimeValue(value);
                    }
                    if (!value) {
                        newRow[colTitle] = '';
                    }
                    else {
                        const colFormat = keyToTitleMap[colKey]?.format;
                        const columnStyle = getColumnStyle(colKey, columnsStyles);
                        newRow[colTitle] = resolveExportCellValue({
                            value,
                            format: colFormat,
                            suffix,
                            columnType: column.type,
                            columnStyle,
                            locale,
                            keepFormat,
                        });
                    }
                }
            });
        }
        return newRow;
    });
};
export const getQueryParams = (query, filterParams, chartQueryFilter, // adjust type as needed
chartPresetFilters // new structure for preset filters
) => {
    // 1. Extract base filters and timeDimensions from the original query.
    const baseFilters = query?.filters ? [...query.filters] : [];
    const baseTimeDimensions = query?.timeDimensions ? [...query.timeDimensions] : [];
    // 2. Merge filters from filterParams.
    // Use a composite key "member|operator" to keep filters distinct.
    const filtersMap = {};
    baseFilters.forEach((filter) => {
        if (filter.member && filter.operator) {
            const key = `${filter.member}|${filter.operator}`;
            filtersMap[key] = filter;
        }
    });
    filterParams.forEach((filter) => {
        if (filter.member && filter.operator) {
            // Defensive: even if a caller forgot to normalize before/after date operators
            // at the UI layer, ensure Cube receives strictly-exclusive comparisons (SSP-4116).
            const normalized = normalizeCubeFilterParam(filter);
            const key = `${normalized.member}|${normalized.operator}`;
            if (!filtersMap[key]) {
                filtersMap[key] = { ...normalized };
            }
            else {
                filtersMap[key].values = [...new Set([...filtersMap[key].values, ...(normalized.values ?? [])])];
            }
        }
    });
    // 3. Merge filters from chartQueryFilter.
    if (chartQueryFilter) {
        Object.entries(chartQueryFilter).forEach(([member, value]) => {
            // Since chartQueryFilter doesn't provide an operator, we assume a default operator.
            const defaultOperator = 'equals';
            const compositeKey = `${member}|${defaultOperator}`;
            const newValues = value.map((v) => v.value);
            if (newValues.length === 0) {
                delete filtersMap[compositeKey];
            }
            else {
                if (!filtersMap[compositeKey]) {
                    filtersMap[compositeKey] = {
                        member,
                        operator: defaultOperator,
                        values: newValues,
                    };
                }
                else {
                    filtersMap[compositeKey].values = [...new Set([...filtersMap[compositeKey].values, ...newValues])];
                }
            }
        });
    }
    // 4. Process preset filters using the same merging logic.
    const presetTimeDimensions = [];
    if (chartPresetFilters) {
        chartPresetFilters.forEach((preset) => {
            if (preset.query) {
                if (Array.isArray(preset.query)) {
                    // Old behavior: process each PresetFilter in the array.
                    preset.query.forEach((filter) => {
                        // Handle nested filters structure: { filters: [...] }
                        if ('filters' in filter && filter.filters && Array.isArray(filter.filters)) {
                            filter.filters.forEach((nestedFilter) => {
                                if (nestedFilter.member && nestedFilter.operator) {
                                    const key = `${nestedFilter.member}|${nestedFilter.operator}`;
                                    // Check for both 'values' (plural) and 'value' (singular) for backwards compatibility
                                    const filterValues = nestedFilter.values || nestedFilter.value;
                                    if (filterValues === undefined) {
                                        // Filter without values - just set the filter without values
                                        filtersMap[key] = {
                                            member: nestedFilter.member,
                                            operator: nestedFilter.operator,
                                        };
                                    }
                                    else if (Array.isArray(filterValues) && filterValues.length > 0) {
                                        // Filter with values - merge with existing or create new
                                        if (!filtersMap[key]) {
                                            filtersMap[key] = {
                                                member: nestedFilter.member,
                                                operator: nestedFilter.operator,
                                                values: [...filterValues],
                                            };
                                        }
                                        else {
                                            // Merge values if filter already exists
                                            const existingValues = filtersMap[key].values || [];
                                            filtersMap[key].values = [...new Set([...existingValues, ...filterValues])];
                                        }
                                    }
                                }
                            });
                        }
                        // Handle direct filter structure
                        else if ('member' in filter && 'operator' in filter && filter.member && filter.operator) {
                            const key = `${filter.member}|${filter.operator}`;
                            // Check for both 'values' (plural) and 'value' (singular) for backwards compatibility
                            const filterValues = filter.values || filter.value;
                            if (filterValues === undefined) {
                                // Filter without values - just set the filter without values
                                filtersMap[key] = {
                                    member: filter.member,
                                    operator: filter.operator,
                                };
                            }
                            else if (Array.isArray(filterValues) && filterValues.length > 0) {
                                // Filter with values - merge with existing or create new
                                if (!filtersMap[key]) {
                                    filtersMap[key] = {
                                        member: filter.member,
                                        operator: filter.operator,
                                        values: [...filterValues],
                                    };
                                }
                                else {
                                    // Merge values if filter already exists
                                    const existingValues = filtersMap[key].values || [];
                                    filtersMap[key].values = [...new Set([...existingValues, ...filterValues])];
                                }
                            }
                        }
                    });
                }
                else if (typeof preset.query === 'object' && preset.query !== null) {
                    // New behavior: preset.query is an object (partial query fragment).
                    if (preset.query.timeDimensions && Array.isArray(preset.query.timeDimensions)) {
                        presetTimeDimensions.push(...preset.query.timeDimensions);
                    }
                    if (preset.query.filters && Array.isArray(preset.query.filters)) {
                        preset.query.filters.forEach((filter) => {
                            if (filter.member && filter.operator) {
                                const key = `${filter.member}|${filter.operator}`;
                                if (!filtersMap[key]) {
                                    filtersMap[key] = { ...filter };
                                }
                                else {
                                    // Merge values if filter already exists
                                    const existingValues = filtersMap[key].values || [];
                                    const newValues = filter.values || [];
                                    filtersMap[key].values = [...new Set([...existingValues, ...newValues])];
                                }
                            }
                        });
                    }
                }
            }
        });
    }
    // 5. Convert the merged filtersMap to an array (filter out filters without values where needed).
    //    A final normalization sweep also catches any preset-filter path that inserted a raw
    //    `beforeDate` / `afterDate` operator (SSP-4116).
    const finalFilters = Object.values(filtersMap)
        .filter((filter) => {
        // Keep filters that either have no values property (operator-only filters) or have non-empty values array
        return (!Object.prototype.hasOwnProperty.call(filter, 'values') ||
            (Array.isArray(filter.values) && filter.values.length > 0));
    })
        .map((filter) => normalizeCubeFilterParam(filter));
    // 6. Return the final query.
    return {
        ...query,
        filters: finalFilters,
        timeDimensions: presetTimeDimensions.length > 0 ? presetTimeDimensions : baseTimeDimensions,
    };
};
/** ISO timestamp safe for filenames (colons and dots replaced), shared across export entry points. */
export const getExportTimestamp = () => new Date().toISOString().replace(/[:.]/g, '-');
/** Sanitizes a chart/dashboard name into a filesystem-safe export filename segment. */
export const sanitizeFileName = (value) => {
    const fallback = 'untitled';
    if (!value) {
        return fallback;
    }
    const sanitized = value
        .trim()
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    return sanitized || fallback;
};
export const exportJsonToCsv = (jsonData, filename = 'export.csv') => {
    if (!jsonData || jsonData.length === 0) {
        console.error('Invalid JSON data');
        return;
    }
    // Extract headers (keys) from the first object
    const headers = Object.keys(jsonData[0]);
    // Map JSON data to CSV rows
    const csvRows = jsonData.map((row) => headers.map((fieldName) => JSON.stringify(row[fieldName] || '')).join(','));
    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    // Create a Blob and trigger download
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
export const filterAndKeepKeys = (data, keys) => {
    return {
        // Remove specified keys (excluding keys array)
        filteredOut: data.map((obj) => Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key)))),
        // Keep only specified keys (excluding anything not in keys array)
        keptOnly: data.map((obj) => Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)))),
    };
};
export const extractDataByX = (data, xValue) => {
    return data
        .map((item) => {
        const matchingSeries = item.series.find((seriesItem) => seriesItem.x === xValue);
        return matchingSeries ? { shortTitle: item.shortTitle, key: item.key, value: matchingSeries.value } : null;
    })
        .filter(Boolean);
};
export const findDataIndex = (obj) => {
    if (obj && typeof obj === 'object') {
        // Check if the current object has a dataIndex property
        if (Object.prototype.hasOwnProperty.call(obj, 'dataIndex')) {
            return obj.dataIndex;
        }
        // If the object is an array, iterate through each element
        if (Array.isArray(obj)) {
            for (const item of obj) {
                const result = findDataIndex(item);
                if (result !== undefined) {
                    return result;
                }
            }
        }
        else {
            // For a plain object, iterate through its keys
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const result = findDataIndex(obj[key]);
                    if (result !== undefined) {
                        return result;
                    }
                }
            }
        }
    }
    // Return undefined if no dataIndex property is found
    return undefined;
};
export const isConditionalStyle = (style) => {
    return !Array.isArray(style) && 'property' in style && 'condition' in style;
};
/**
 * Normalize a Cube date/time string into a shape every JS engine parses as UTC.
 * Cube returns bare dates (`2026-09-02`) for date-typed measures; a zone suffix
 * after a date-only form is invalid ISO-8601, which JavaScriptCore rejects
 * (Safari rendered empty table cells, SSP-4319) while V8 accepts it. A bare date
 * already parses as UTC midnight per spec, so it needs no suffix. Date-times get
 * the SQL space separator normalized to `T` and a `Z` appended unless already
 * zoned, so they are read as UTC rather than local time.
 */
export const toUtcDateString = (dateString) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    const isoString = dateString.replace(' ', 'T');
    return isoString.endsWith('Z') ? isoString : isoString + 'Z';
};
export const parseISODate = (dateString) => {
    if (!dateString) {
        return '';
    }
    // Format as YYYY-MM-DD in UTC timezone (en-CA locale gives YYYY-MM-DD format)
    return new Date(toUtcDateString(dateString)).toLocaleDateString('en-CA', { timeZone: 'UTC' });
};
/**
 * Format an ISO timestamp as `YYYY-MM-DD HH:mm:ss` in UTC. Used to render Cube.js
 * TIME dimensions in tables without truncating the time-of-day (SSP-4116).
 * Returns an empty string for empty / invalid input so callers can render as
 * "no value" rather than "Invalid Date".
 */
export const parseISODateTime = (dateString) => {
    if (!dateString) {
        return '';
    }
    const date = new Date(toUtcDateString(dateString));
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const pad = (n) => n.toString().padStart(2, '0');
    return (`${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
        `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`);
};
